import { useState } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  FileText,
  Download,
  Loader2,
  Shield,
  Sparkles,
  AlertTriangle,
  CheckCircle2,
} from "lucide-react";

const SDSGenerator = () => {
  const [perfumeCode, setPerfumeCode] = useState("");
  const [ufiCode, setUfiCode] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedPdf, setGeneratedPdf] = useState(null);
  const [recentGenerations, setRecentGenerations] = useState([]);

  const handleGenerate = async () => {
    if (!perfumeCode.trim()) {
      toast.error("Inserisci un codice profumo");
      return;
    }

    setIsGenerating(true);
    setGeneratedPdf(null);

    try {
      const payload = {
        perfume_code: perfumeCode.trim(),
      };
      
      // Add UFI code only if provided
      if (ufiCode.trim()) {
        payload.ufi_code = ufiCode.trim();
      }

      const res = await api.post("/sds/generate", payload);

      if (res.data && res.data.success) {
        setGeneratedPdf(res.data.pdf);
        toast.success(res.data.message || `SDS generata per ${perfumeCode.toUpperCase()}`);
        
        // Add to recent generations
        setRecentGenerations(prev => {
          const updated = [
            { 
              code: perfumeCode.toUpperCase(), 
              ufi: ufiCode.trim() || null,
              timestamp: new Date(), 
              pdf: res.data.pdf 
            },
            ...prev.filter(g => g.code !== perfumeCode.toUpperCase())
          ].slice(0, 10);
          return updated;
        });
      } else {
        toast.error(res.data?.detail || "Errore nella generazione");
      }
    } catch (error) {
      console.error("SDS generation error:", error);
      toast.error(error.response?.data?.detail || "Errore nella generazione della SDS");
    } finally {
      setIsGenerating(false);
    }
  };

  const downloadPdf = (pdfData, filename) => {
    const byteCharacters = atob(pdfData.data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: "application/pdf" });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || pdfData.filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast.success(`Download: ${filename || pdfData.filename}`);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleGenerate();
    }
  };

  return (
    <div data-testid="sds-generator-page" className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div>
        <h1 className="text-3xl md:text-4xl font-serif text-gray-900 mb-2">
          Generatore SDS
        </h1>
        <p className="text-gray-500">
          Genera Schede Dati di Sicurezza per i tuoi profumi
        </p>
      </div>

      {/* Main Card */}
      <Card className="bg-white border-gray-200 shadow-sm">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-gradient-to-br from-emerald-500/20 to-emerald-500/5 rounded-xl">
              <Shield className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <CardTitle className="text-gray-900 text-xl">
                Scheda Dati di Sicurezza (SDS)
              </CardTitle>
              <p className="text-sm text-gray-500 mt-1">
                Conforme al Regolamento (UE) 2020/878
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Input Section */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Codice Profumo <span className="text-red-500">*</span>
              </label>
              <Input
                type="text"
                placeholder="Es: 8000, M57, C0K, #31#..."
                value={perfumeCode}
                onChange={(e) => setPerfumeCode(e.target.value.toUpperCase())}
                onKeyPress={handleKeyPress}
                className="text-lg font-mono uppercase"
                data-testid="sds-code-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Codice UFI <span className="text-gray-400 text-xs font-normal">(opzionale)</span>
              </label>
              <Input
                type="text"
                placeholder="Es: XXXX-XXXX-XXXX-XXXX"
                value={ufiCode}
                onChange={(e) => setUfiCode(e.target.value.toUpperCase())}
                onKeyPress={handleKeyPress}
                className="text-lg font-mono uppercase"
                data-testid="sds-ufi-input"
              />
              <p className="text-xs text-gray-400 mt-1">
                Se vuoto, resta il codice UFI originale
              </p>
            </div>
          </div>
          
          {/* Generate Button */}
          <div className="flex justify-end">
            <Button
              onClick={handleGenerate}
              disabled={isGenerating || !perfumeCode.trim()}
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-8"
              data-testid="sds-generate-btn"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generazione...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Genera SDS
                </>
              )}
            </Button>
          </div>

          {/* Info Box */}
          <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-medium">Informazioni importanti</p>
                <p className="mt-1">
                  La SDS generata è basata su un template standard. Verifica sempre 
                  che le informazioni siano corrette per il prodotto specifico prima 
                  della distribuzione.
                </p>
              </div>
            </div>
          </div>

          {/* Generated PDF Preview */}
          {generatedPdf && (
            <div className="p-6 bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl border border-emerald-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-emerald-100 rounded-xl">
                    <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-emerald-900">
                      SDS Generata con Successo!
                    </h3>
                    <p className="text-sm text-emerald-700">
                      {generatedPdf.filename}
                    </p>
                  </div>
                </div>
                <Button
                  onClick={() => downloadPdf(generatedPdf, generatedPdf.filename)}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="sds-download-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Scarica PDF
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Generations */}
      {recentGenerations.length > 0 && (
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="text-gray-900 text-lg flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Generazioni Recenti
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
              {recentGenerations.map((gen, index) => (
                <button
                  key={index}
                  onClick={() => downloadPdf(gen.pdf, gen.pdf.filename)}
                  className="p-4 bg-gray-50 hover:bg-emerald-50 rounded-xl border border-gray-200 hover:border-emerald-300 transition-all text-center group"
                >
                  <FileText className="w-8 h-8 text-gray-400 group-hover:text-emerald-600 mx-auto mb-2 transition-colors" />
                  <p className="font-mono font-bold text-gray-900">{gen.code}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {gen.timestamp.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Instructions */}
      <Card className="bg-gray-50 border-gray-200">
        <CardContent className="py-6">
          <h3 className="font-semibold text-gray-900 mb-4">Come funziona</h3>
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold text-sm">1</div>
              <div>
                <p className="font-medium text-gray-900">Inserisci il codice</p>
                <p className="text-sm text-gray-500">Digita il codice del profumo (es: 8000, M57)</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold text-sm">2</div>
              <div>
                <p className="font-medium text-gray-900">Genera la SDS</p>
                <p className="text-sm text-gray-500">Clicca "Genera SDS" per creare il documento</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold text-sm">3</div>
              <div>
                <p className="font-medium text-gray-900">Scarica il PDF</p>
                <p className="text-sm text-gray-500">Scarica la scheda pronta per l'uso</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SDSGenerator;
